import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Typography } from '@material-ui/core'
import { useSelector } from 'react-redux'
import { Button, Input, Loading } from '../root/components/common'
import { CustomSwitch2 } from '../root/components/common/switch'
import { UserPermissions } from '../../services/permissions'
import {
    useIntegrationSettings,
    usePatchIntegrationSettings
} from './hooks/use-integration-settings'
import { useBillingConfirmation } from './hooks/use-billing-confirmation'
import BillingInfoModal from './billing-info-modal'
import IntegrationHint from './integration-hint'

const useStyles = makeStyles(theme => ({
    section: {
        border: theme.border,
        borderRadius: theme.borderRadius,
        padding: 24,
        marginBottom: 16
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    sectionTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        color: theme.textPrimary,
        fontFamily: theme.fontFamily
    },
    credentialsForm: {
        marginTop: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 16
    },
    credentialsRow: {
        display: 'flex',
        gap: 16
    },
    credentialField: {
        flex: 1
    },
    saveRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12
    },
    warning: {
        marginTop: 12,
        color: theme.error,
        fontSize: 13
    }
}))

export default function DocumentationTools() {
    const classes = useStyles()
    const locale = useSelector(state => state.main.locale)
    const { original_customer_id: customerId } = useSelector(
        state => state.main.user
    )

    const canUpdate = UserPermissions.location(customerId).update

    const { data, isLoading } = useIntegrationSettings(customerId)
    const { mutate, isLoading: isSaving } = usePatchIntegrationSettings(
        customerId
    )
    const billingConfirmation = useBillingConfirmation()

    const [debuxxUsername, setDebuxxUsername] = useState('')
    const [debuxxPassword, setDebuxxPassword] = useState('')
    const [isPasswordFocused, setIsPasswordFocused] = useState(false)

    const [pestscanPcoId, setPestscanPcoId] = useState('')

    useEffect(() => {
        setDebuxxUsername(data?.debuxx_credentials?.username ?? '')
        setDebuxxPassword('')
    }, [data?.debuxx_credentials?.username])

    useEffect(() => {
        setPestscanPcoId(data?.pestscan_pco_id ?? '')
    }, [data?.pestscan_pco_id])

    if (isLoading || !data) {
        return <Loading />
    }

    const toggleIntegration = (key, checked) => {
        const run = () => mutate({ [key]: checked })
        if (checked) {
            billingConfirmation.requestConfirmation(run)
        } else {
            run()
        }
    }

    const handleTogglePestsoft = (_event, checked) => {
        toggleIntegration('pestsoft', checked)
    }

    const handleToggleDebuxx = (_event, checked) => {
        toggleIntegration('debuxx', checked)
    }

    const storedUsername = data.debuxx_credentials?.username ?? ''
    const credsHasUsername = storedUsername.length > 0
    const credsHasPassword = Boolean(data.debuxx_credentials?.has_password)
    const isDebuxxFullyConfigured = credsHasUsername && credsHasPassword

    const usernameDirty = debuxxUsername !== storedUsername
    const passwordDirty = debuxxPassword.length > 0

    const canSaveDebuxxCreds =
        canUpdate &&
        !isSaving &&
        debuxxUsername.length > 0 &&
        (usernameDirty || passwordDirty)

    const showDebuxxCredsWarning =
        data.debuxx === true && !isDebuxxFullyConfigured

    const showStoredPasswordMask =
        credsHasPassword && !isPasswordFocused && debuxxPassword === ''
    const passwordDisplayValue = showStoredPasswordMask
        ? '••••••••'
        : debuxxPassword

    const handleSaveDebuxxCreds = () => {
        const credentials = {}
        if (usernameDirty) credentials.username = debuxxUsername
        if (passwordDirty) credentials.password = debuxxPassword
        mutate(
            { debuxx_credentials: credentials },
            { onSuccess: () => setDebuxxPassword('') }
        )
    }

    const pestscanEnabled = Boolean(data.pestscan)
    const storedPestscanPcoId = data.pestscan_pco_id ?? ''
    const trimmedPcoId = pestscanPcoId.trim()
    const pcoIdDirty = trimmedPcoId !== storedPestscanPcoId

    const showPestscanIncompleteWarning =
        pestscanEnabled && trimmedPcoId.length === 0

    const canSavePestscanPcoId =
        canUpdate && !isSaving && pcoIdDirty && trimmedPcoId.length > 0

    const handleTogglePestscan = (_event, checked) => {
        toggleIntegration('pestscan', checked)
    }

    const handleSavePestscanPcoId = () => {
        mutate({ pestscan_pco_id: trimmedPcoId })
    }

    return (
        <div>
            <div className={classes.section}>
                <div className={classes.sectionHeader}>
                    <Typography className={classes.sectionTitle}>
                        {locale.integrations.pestsoft}
                    </Typography>
                    <CustomSwitch2
                        checked={Boolean(data.pestsoft)}
                        onChange={handleTogglePestsoft}
                        disabled={!canUpdate || isSaving}
                    />
                </div>
            </div>

            <div className={classes.section}>
                <div className={classes.sectionHeader}>
                    <Typography className={classes.sectionTitle}>
                        {locale.integrations.debuxx}
                    </Typography>
                    <CustomSwitch2
                        checked={Boolean(data.debuxx)}
                        onChange={handleToggleDebuxx}
                        disabled={!canUpdate || isSaving}
                    />
                </div>

                <IntegrationHint
                    toggleLabel={locale.integrations.debuxxHint.toggle}
                    title={locale.integrations.debuxxHint.title}
                    body={locale.integrations.debuxxHint.body}
                    support={locale.integrations.debuxxHint.support}
                />

                {showDebuxxCredsWarning && (
                    <div className={classes.warning}>
                        {locale.integrations.credentialsRequired}
                    </div>
                )}

                <div className={classes.credentialsForm}>
                    <div className={classes.credentialsRow}>
                        <div className={classes.credentialField}>
                            <Input
                                label={locale.integrations.username}
                                type="text"
                                value={debuxxUsername}
                                setValue={setDebuxxUsername}
                                disabled={!canUpdate || isSaving}
                            />
                        </div>
                        <div className={classes.credentialField}>
                            <Input
                                label={locale.common.password}
                                type="password"
                                value={passwordDisplayValue}
                                setValue={setDebuxxPassword}
                                placeholder={
                                    credsHasPassword
                                        ? undefined
                                        : locale.integrations.enterPassword
                                }
                                InputLabelProps={{ shrink: true }}
                                onFocus={() => setIsPasswordFocused(true)}
                                onBlur={() => setIsPasswordFocused(false)}
                                disabled={!canUpdate || isSaving}
                            />
                        </div>
                    </div>
                    <div className={classes.saveRow}>
                        <Button
                            onClick={handleSaveDebuxxCreds}
                            disabled={!canSaveDebuxxCreds}
                        >
                            {locale.common.save}
                        </Button>
                    </div>
                </div>
            </div>

            <div className={classes.section}>
                <div className={classes.sectionHeader}>
                    <Typography className={classes.sectionTitle}>
                        {locale.integrations.pestscan}
                    </Typography>
                    <CustomSwitch2
                        checked={pestscanEnabled}
                        onChange={handleTogglePestscan}
                        disabled={!canUpdate || isSaving}
                    />
                </div>

                <IntegrationHint
                    toggleLabel={locale.integrations.pestscanHint.toggle}
                    title={locale.integrations.pestscanHint.title}
                    steps={locale.integrations.pestscanHint.steps}
                    support={locale.integrations.pestscanHint.support}
                />

                {showPestscanIncompleteWarning && (
                    <div className={classes.warning}>
                        {locale.integrations.pestscanPcoIdRequiredWhenEnabled}
                    </div>
                )}
                <div className={classes.credentialsForm}>
                    <Input
                        label={locale.integrations.pestscanPcoId}
                        type="text"
                        value={pestscanPcoId}
                        setValue={setPestscanPcoId}
                        disabled={!canUpdate || isSaving}
                    />
                </div>
                <div className={classes.saveRow} style={{ marginTop: 24 }}>
                    <Button
                        onClick={handleSavePestscanPcoId}
                        disabled={!canSavePestscanPcoId}
                    >
                        {locale.common.save}
                    </Button>
                </div>
            </div>

            <BillingInfoModal
                open={billingConfirmation.isOpen}
                onConfirm={billingConfirmation.confirm}
                onCancel={billingConfirmation.cancel}
            />
        </div>
    )
}
